import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, Download, Upload, MessageCircle, LogOut, Package, Edit3, Trash2,
  X, ImageDown, Copy, ArrowUp, ArrowDown, ArrowUpDown, Feather, Layers, CheckSquare,
  ChevronLeft, ChevronRight, CopyPlus,
} from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { AccountingLayout } from '../components/AccountingLayout';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import {
  listSalesProducts, listSalesBrands, listSalesCategories, salesProductStats,
  createSalesProduct, updateSalesProduct, deleteSalesProduct, importSalesProducts,
  bulkDeleteSalesProducts,
  listCatalogPresets, createCatalogPreset, deleteCatalogPreset,
} from '../lib/api';

/* ---------------- helpers ---------------- */
const fmtRp = (n) => {
  if (n === null || n === undefined || isNaN(Number(n))) return 'Rp 0';
  return 'Rp ' + Math.round(Number(n)).toLocaleString('id-ID');
};
const parseNum = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return v;
  const s = String(v).replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.');
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
};
const formatThousands = (v) => {
  const s = String(v).replace(/\D/g, '');
  return s ? Number(s).toLocaleString('id-ID') : '';
};
const computeNett = (price, d1, d2, d3, d4) => {
  let p = Number(price || 0);
  for (const d of [d1, d2, d3, d4]) {
    const dd = Number(d || 0);
    if (dd > 0) p = p * (1 - dd / 100);
  }
  return Math.round(p);
};
const discountLabel = (d1, d2, d3, d4) => {
  const parts = [d1, d2, d3, d4].map((x) => Number(x || 0)).filter((x) => x > 0);
  return parts.length ? parts.map((x) => `${x}%`).join(' + ') : '—';
};
const todayStr = () => new Date().toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' });

/* ---------------- page ---------------- */
export default function SalesCatalogPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_category: {} });
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [brand, setBrand] = useState('all');
  const [statusFilter, setStatusFilter] = useState(user?.role === 'admin' ? 'all' : 'active');
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [editing, setEditing] = useState(null); // { ...product } | 'new' | null
  const [confirmDel, setConfirmDel] = useState(null);
  const [confirmBulkDel, setConfirmBulkDel] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  const [presetFields, setPresetFields] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [sort, setSort] = useState({ key: null, dir: 'asc' });

  const isAdmin = user?.role === 'admin';

  /* ---------- data loading ---------- */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (query.trim()) params.q = query.trim();
      if (category !== 'all') params.category = category;
      if (brand !== 'all') params.brand = brand;
      if (statusFilter !== 'all') params.status = statusFilter;
      const r = await listSalesProducts(params);
      setProducts(r);
    } catch {
      toast.error('Gagal memuat produk');
    } finally { setLoading(false); }
  }, [query, category, brand, statusFilter]);

  const loadMeta = useCallback(async () => {
    try {
      const [c, b, s] = await Promise.all([
        listSalesCategories(), listSalesBrands(), salesProductStats(),
      ]);
      setCategories(c);
      setBrands(b);
      setStats(s);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { setPage(1); }, [query, category, brand, statusFilter]);

  const sortedProducts = useMemo(() => {
    if (!sort.key) return products;
    const arr = [...products];
    const getVal = (p) => {
      switch (sort.key) {
        case 'name':       return (p.name || '').toLowerCase();
        case 'category':   return (p.category || '').toLowerCase();
        case 'brand':      return (p.brand || '').toLowerCase();
        case 'price_list': return Number(p.price_list) || 0;
        case 'discount': {
          const pl = Number(p.price_list) || 0;
          const nt = Number(p.nett_price) || 0;
          return pl > 0 ? (1 - nt / pl) * 100 : 0;
        }
        case 'nett_price': return Number(p.nett_price) || 0;
        case 'package':    return (p.package_free || '').toLowerCase();
        default: return 0;
      }
    };
    arr.sort((a, b) => {
      const av = getVal(a); const bv = getVal(b);
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [products, sort]);

  const paged = useMemo(() => {
    const start = (page - 1) * perPage;
    return sortedProducts.slice(start, start + perPage);
  }, [sortedProducts, page, perPage]);
  const totalPages = Math.max(1, Math.ceil(products.length / perPage));

  const toggleSort = (key) => setSort((s) => {
    if (s.key !== key) return { key, dir: 'asc' };
    if (s.dir === 'asc') return { key, dir: 'desc' };
    return { key: null, dir: 'asc' };
  });

  /* ---------- selection ---------- */
  const isSelected = (id) => selectedIds.has(id);
  const toggle = (id) => setSelectedIds((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleAllVisible = () => {
    const allSelected = paged.every((p) => selectedIds.has(p.id));
    setSelectedIds((s) => {
      const n = new Set(s);
      if (allSelected) paged.forEach((p) => n.delete(p.id));
      else paged.forEach((p) => n.add(p.id));
      return n;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const selectedProducts = useMemo(
    () => sortedProducts.filter((p) => selectedIds.has(p.id)),
    [sortedProducts, selectedIds]
  );

  /* ---------- delete ---------- */
  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await deleteSalesProduct(confirmDel.id);
      toast.success('Produk dihapus');
      setConfirmDel(null);
      setSelectedIds((s) => { const n = new Set(s); n.delete(confirmDel.id); return n; });
      load();
      loadMeta();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menghapus');
    }
  };

  /* ---------- toggle status ---------- */
  const toggleStatus = async (p) => {
    const next = p.status === 'active' ? 'inactive' : 'active';
    try {
      await updateSalesProduct(p.id, {
        category: p.category, brand: p.brand, name: p.name,
        price_list: p.price_list, disc1: p.disc1, disc2: p.disc2,
        disc3: p.disc3, disc4: p.disc4, nett_price: p.nett_price,
        package_free: p.package_free, status: next,
      });
      toast.success(next === 'active' ? 'Produk diaktifkan' : 'Produk dinonaktifkan');
      load();
    } catch { toast.error('Gagal ubah status'); }
  };

  /* ---------- duplicate (clone) ---------- */
  const duplicateProduct = (p) => {
    // Open the form as a NEW product, pre-filled from source. `id` is stripped
    // so ProductFormModal treats this as create (isEdit = !!initial?.id).
    setEditing({
      category:     p.category,
      brand:        p.brand,
      name:         (p.name || '') + ' (COPY)',
      price_list:   p.price_list,
      disc1:        p.disc1,
      disc2:        p.disc2,
      disc3:        p.disc3,
      disc4:        p.disc4,
      nett_price:   p.nett_price,
      package_free: p.package_free,
      status:       p.status || 'active',
    });
  };

  /* ---------- bulk delete ---------- */
  const doBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) { setConfirmBulkDel(false); return; }
    try {
      const r = await bulkDeleteSalesProducts(ids);
      toast.success(`${r.deleted} produk dihapus`);
      setConfirmBulkDel(false);
      clearSelection();
      load();
      loadMeta();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menghapus banyak produk');
    }
  };

  /* ---------- save ---------- */
  const onSaved = () => { load(); loadMeta(); };

  /* ---------- export ---------- */
  const doExport = (fmt, scope) => {
    const rows = scope === 'selected' ? selectedProducts : sortedProducts;
    if (!rows.length) { toast.error('Tidak ada produk untuk diekspor'); return; }
    const headers = ['ID', 'Kategori', 'Brand', 'Produk', 'Price List',
      'Discount 1', 'Discount 2', 'Discount 3', 'Discount 4', 'Nett Price',
      'Paket / Free', 'Status', 'Dibuat', 'Diubah'];
    const body = rows.map((p) => [
      p.id, p.category || '', p.brand || '', p.name || '',
      p.price_list || 0, p.disc1 || 0, p.disc2 || 0, p.disc3 || 0, p.disc4 || 0,
      p.nett_price || 0, p.package_free || '', p.status || 'active',
      (p.created_at || '').slice(0, 10), (p.updated_at || '').slice(0, 10),
    ]);
    const stamp = new Date().toISOString().slice(0, 10);
    if (fmt === 'csv') {
      const csv = [headers, ...body].map((r) =>
        r.map((v) => {
          const s = String(v ?? '');
          return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        }).join(',')
      ).join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Sales_Price_Catalog_${stamp}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } else {
      const ws = XLSX.utils.aoa_to_sheet([headers, ...body]);
      ws['!cols'] = [
        { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 34 },
        { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 },
        { wch: 14 }, { wch: 16 }, { wch: 10 }, { wch: 12 }, { wch: 12 },
      ];
      const rpFmt = '"Rp"#,##0';
      const pctFmt = '0.0"%"';
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let c = 0; c <= range.e.c; c += 1) {
        const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
        if (cell) cell.s = {
          font: { bold: true, color: { rgb: 'FFFFFFFF' } },
          fill: { patternType: 'solid', fgColor: { rgb: 'FF0F172A' } },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      }
      const rpCols = [4, 9];
      const pctCols = [5, 6, 7, 8];
      for (let r = 1; r <= range.e.r; r += 1) {
        for (const c of rpCols) {
          const cell = ws[XLSX.utils.encode_cell({ r, c })];
          if (cell && typeof cell.v === 'number') cell.z = rpFmt;
        }
        for (const c of pctCols) {
          const cell = ws[XLSX.utils.encode_cell({ r, c })];
          if (cell && typeof cell.v === 'number') cell.z = pctFmt;
        }
      }
      ws['!freeze'] = { xSplit: 0, ySplit: 1 };
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Products');
      XLSX.writeFile(wb, `Sales_Price_Catalog_${stamp}.xlsx`);
    }
    toast.success(`${rows.length} produk diekspor`);
    setShowExport(false);
  };

  /* ---------- render ---------- */
  const catCount = (c) => stats.by_category?.[c] || 0;
  const isSalesRole = user?.role === 'sales';

  return (
    <AccountingLayout fullWidth={true}>
      <div className={`space-y-5 ${selectedIds.size > 0 ? 'pb-24' : ''}`} data-testid="sales-catalog-page">
        {/* Title row + actions */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Sales Price Catalog</h1>
            <p className="text-sm text-slate-500 mt-1">
              Database Produk · Price List · Discount · Nett Price
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setEditing('new')}
              className="h-10 px-4 rounded-lg bg-emerald-800 hover:bg-emerald-900 text-white text-sm font-semibold inline-flex items-center gap-2 shadow-sm"
              data-testid="sales-add-btn"
            >
              <Plus className="w-4 h-4" /> Tambah Produk
            </button>
            <button
              onClick={() => setShowImport(true)}
              className="h-10 px-4 rounded-lg bg-white hover:bg-slate-50 text-slate-800 text-sm font-semibold border border-slate-200 inline-flex items-center gap-2"
              data-testid="sales-import-btn"
            >
              <Upload className="w-4 h-4" /> Import
            </button>
            <button
              onClick={() => setShowExport(true)}
              className="h-10 px-4 rounded-lg bg-white hover:bg-slate-50 text-slate-800 text-sm font-semibold border border-slate-200 inline-flex items-center gap-2"
              data-testid="sales-export-btn"
            >
              <Download className="w-4 h-4" /> Export
            </button>
            {isSalesRole && (
              <button
                onClick={logout}
                className="h-10 px-3 rounded-lg text-slate-500 hover:text-slate-800 text-sm inline-flex items-center gap-1.5"
                data-testid="sales-logout-btn"
              >
                <LogOut className="w-4 h-4" /> Logout
              </button>
            )}
          </div>
        </div>

        {/* Search + 2 dropdowns */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cari produk atau brand…"
                className="h-11 pl-10 rounded-lg border-slate-200 bg-white"
                data-testid="sales-search"
              />
              {query && (
                <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100">
                  <X className="w-3.5 h-3.5 text-slate-400" />
                </button>
              )}
            </div>
          </div>
          <div className="md:col-span-3">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="sales-filter-category"
            >
              <option value="all">Semua Kategori</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="md:col-span-3">
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="sales-filter-brand"
            >
              <option value="all">Semua Brand</option>
              {brands.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total Produk"     value={stats.total}         icon={Package}      tone="emerald" />
          <StatCard label="Raket"            value={catCount('Raket')}   icon={Layers}       tone="green" />
          <StatCard label="Senar"            value={catCount('Senar')}   icon={Feather}      tone="amber" />
          <StatCard label="Produk Dipilih"   value={selectedIds.size}    icon={CheckSquare}  tone="emerald" />
        </div>

        {/* Selection bar */}
        {selectedIds.size > 0 && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm font-medium text-emerald-900" data-testid="sales-selection-count">
              {selectedIds.size} produk dipilih
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearSelection}
                className="text-xs font-medium text-emerald-800 hover:text-emerald-900 px-3 py-1.5 rounded-md hover:bg-emerald-100 transition"
                data-testid="sales-clear-selection"
              >
                Hapus Pilihan
              </button>
              <button
                onClick={() => setShowCatalog(true)}
                className="text-xs font-semibold bg-emerald-800 text-white px-3 py-1.5 rounded-md hover:bg-emerald-900 transition inline-flex items-center gap-1.5"
                data-testid="sales-open-catalog"
              >
                <MessageCircle className="w-3.5 h-3.5" /> Buat Katalog
              </button>
            </div>
          </div>
        )}

        {/* Products */}
        {loading ? (
          <div className="text-sm text-slate-500 py-10 text-center">Memuat…</div>
        ) : products.length === 0 ? (
          <EmptyState onAdd={() => setEditing('new')} onImport={() => setShowImport(true)} />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block rounded-lg bg-white border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="w-10 px-3 py-3">
                      <input
                        type="checkbox"
                        checked={paged.length > 0 && paged.every((p) => selectedIds.has(p.id))}
                        onChange={toggleAllVisible}
                        className="w-4 h-4 rounded border-slate-300 text-emerald-700 focus:ring-emerald-500"
                        data-testid="sales-select-all"
                      />
                    </th>
                    <SortTh label="Produk"     sortKey="name"       sort={sort} onSort={toggleSort} />
                    <SortTh label="Kategori"   sortKey="category"   sort={sort} onSort={toggleSort} />
                    <SortTh label="Brand"      sortKey="brand"      sort={sort} onSort={toggleSort} />
                    <SortTh label="Price List" sortKey="price_list" sort={sort} onSort={toggleSort} align="right" />
                    <SortTh label="Discount"   sortKey="discount"   sort={sort} onSort={toggleSort} align="right" />
                    <SortTh label="Netto"      sortKey="nett_price" sort={sort} onSort={toggleSort} align="right" />
                    <SortTh label="Paket"      sortKey="package"    sort={sort} onSort={toggleSort} />
                    <Th className="text-right w-32">Aksi</Th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((p) => (
                    <tr key={p.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${p.status === 'inactive' ? 'opacity-50' : ''}`}
                      data-testid={`sales-row-${p.id}`}>
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected(p.id)}
                          onChange={() => toggle(p.id)}
                          className="w-4 h-4 rounded border-slate-300 text-emerald-700 focus:ring-emerald-500"
                          data-testid={`sales-check-${p.id}`}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-semibold text-slate-900">{p.name}</div>
                      </td>
                      <td className="px-3 py-3 text-slate-700">{p.category || '—'}</td>
                      <td className="px-3 py-3 text-slate-700 font-medium">{p.brand || '—'}</td>
                      <td className="px-3 py-3 text-right font-mono text-slate-700">{fmtRp(p.price_list)}</td>
                      <td className="px-3 py-3 text-right">
                        <span className="text-red-600 font-medium text-xs">{discountLabel(p.disc1, p.disc2, p.disc3, p.disc4)}</span>
                      </td>
                      <td className="px-3 py-3 text-right font-mono font-bold text-slate-900">{fmtRp(p.nett_price)}</td>
                      <td className="px-3 py-3 text-slate-600 text-xs">{p.package_free || '—'}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <IconBtn title="Edit" onClick={() => setEditing(p)} testid={`sales-edit-${p.id}`}>
                            <Edit3 className="w-3.5 h-3.5" />
                          </IconBtn>
                          <IconBtn title="Duplikat" onClick={() => duplicateProduct(p)} testid={`sales-dup-${p.id}`}>
                            <CopyPlus className="w-3.5 h-3.5" />
                          </IconBtn>
                          <Switch
                            checked={p.status === 'active'}
                            onCheckedChange={() => toggleStatus(p)}
                            className="data-[state=checked]:bg-emerald-600"
                            data-testid={`sales-toggle-${p.id}`}
                          />
                          <IconBtn title="Hapus" danger onClick={() => setConfirmDel(p)} testid={`sales-del-${p.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </IconBtn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {paged.map((p) => (
                <div key={p.id} className={`rounded-lg bg-white border p-4 ${isSelected(p.id) ? 'border-emerald-500' : 'border-slate-200'} ${p.status === 'inactive' ? 'opacity-50' : ''}`}
                  data-testid={`sales-card-${p.id}`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected(p.id)}
                      onChange={() => toggle(p.id)}
                      className="w-4 h-4 mt-1 rounded border-slate-300 text-emerald-700 focus:ring-emerald-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">{p.brand || 'No Brand'}</div>
                      <div className="font-bold text-slate-900 truncate">{p.name}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{p.category || '—'}</div>
                    </div>
                    <Switch
                      checked={p.status === 'active'}
                      onCheckedChange={() => toggleStatus(p)}
                      className="data-[state=checked]:bg-emerald-600"
                    />
                  </div>
                  <div className="mt-3 space-y-1.5 text-sm">
                    <Row label="Price List" value={<span className="font-mono text-slate-700">{fmtRp(p.price_list)}</span>} />
                    <Row label="Discount" value={<span className="text-red-600 font-medium">{discountLabel(p.disc1, p.disc2, p.disc3, p.disc4)}</span>} />
                    <Row label="Netto" value={<span className="font-mono font-bold text-slate-900 text-base">{fmtRp(p.nett_price)}</span>} highlight />
                    {p.package_free && <Row label="Paket" value={<span className="text-slate-600 text-xs">{p.package_free}</span>} />}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => setEditing(p)} className="flex-1 h-9 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium">Edit</button>
                    <button onClick={() => duplicateProduct(p)} className="h-9 px-3 rounded-lg border border-emerald-200 text-emerald-800 text-sm font-medium inline-flex items-center gap-1.5" data-testid={`sales-dup-card-${p.id}`}>
                      <CopyPlus className="w-3.5 h-3.5" /> Duplikat
                    </button>
                    <button onClick={() => setConfirmDel(p)} className="h-9 px-3 rounded-lg border border-red-200 text-red-700 text-sm font-medium">Hapus</button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between flex-wrap gap-3 text-sm">
              <div className="text-slate-500">
                Menampilkan {(page - 1) * perPage + 1}–{Math.min(page * perPage, products.length)} dari {products.length}
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={perPage}
                  onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                  className="h-9 rounded-lg border border-slate-200 px-2 text-sm bg-white"
                  data-testid="sales-per-page"
                >
                  <option value={20}>20/hal</option>
                  <option value={50}>50/hal</option>
                  <option value={100}>100/hal</option>
                </select>
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="h-9 w-9 rounded-lg border border-slate-200 disabled:opacity-40 bg-white inline-flex items-center justify-center"
                  data-testid="sales-page-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <PageNumbers current={page} total={totalPages} onPage={setPage} />
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="h-9 w-9 rounded-lg border border-slate-200 disabled:opacity-40 bg-white inline-flex items-center justify-center"
                  data-testid="sales-page-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}

      {/* Modals */}
      {editing && (
        <ProductFormModal
          initial={editing === 'new' ? null : editing}
          categories={categories}
          brandOptions={brands}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onSaved(); }}
        />
      )}
      <DeleteConfirm product={confirmDel} onCancel={() => setConfirmDel(null)} onConfirm={doDelete} />
      {/* Bulk delete confirm */}
      <Dialog open={confirmBulkDel} onOpenChange={(v) => !v && setConfirmBulkDel(false)}>
        <DialogContent className="max-w-sm" data-testid="sales-bulk-del-modal">
          <DialogHeader>
            <DialogTitle>Hapus {selectedIds.size} produk terpilih?</DialogTitle>
            <DialogDescription>
              Semua {selectedIds.size} produk yang dicentang akan dihapus permanen dari database. Aksi ini tidak bisa dibatalkan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmBulkDel(false)}>Batal</Button>
            <Button
              onClick={doBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="sales-bulk-del-confirm"
            >
              Hapus {selectedIds.size} Produk
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Sticky bottom action bar (Gmail-style) — houses destructive actions safely away from Buat Katalog */}
      {selectedIds.size > 0 && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white rounded-full shadow-2xl border border-slate-800 px-2 py-2 flex items-center gap-2"
          data-testid="sales-bottom-action-bar"
        >
          <div className="pl-3 pr-2 text-xs font-medium text-slate-200">
            <span className="text-white font-semibold">{selectedIds.size}</span> dipilih
          </div>
          <div className="w-px h-5 bg-slate-700" />
          <button
            onClick={clearSelection}
            className="text-xs font-medium text-slate-200 hover:text-white hover:bg-slate-800 px-3 py-1.5 rounded-full transition"
            data-testid="sales-bottom-clear-selection"
          >
            Batal
          </button>
          <button
            onClick={() => setConfirmBulkDel(true)}
            className="text-xs font-semibold text-slate-100 hover:text-white hover:bg-slate-800 px-3 py-1.5 rounded-full transition inline-flex items-center gap-1.5"
            data-testid="sales-bulk-delete-btn"
            title="Hapus produk terpilih"
          >
            <Trash2 className="w-3.5 h-3.5" /> Hapus Terpilih
          </button>
        </div>
      )}
      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onDone={() => { setShowImport(false); load(); loadMeta(); }}
        />
      )}
      {showExport && (
        <ExportModal
          onClose={() => setShowExport(false)}
          selectedCount={selectedIds.size}
          onExport={doExport}
        />
      )}
      {showCatalog && (
        <CatalogModal
          items={selectedProducts}
          initialFields={presetFields}
          onLoadPreset={(ids, f) => {
            setSelectedIds(new Set(ids));
            setPresetFields(f || null);
            setShowCatalog(false);
            setTimeout(() => setShowCatalog(true), 120);
            toast.success('Preset dimuat');
          }}
          onClose={() => { setShowCatalog(false); setPresetFields(null); }}
        />
      )}
      </div>
    </AccountingLayout>
  );
}

/* ---------------- Sub Components ---------------- */

const ActionBtn = ({ icon: Icon, label, onClick, primary, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`h-10 px-4 rounded-lg text-sm font-semibold transition inline-flex items-center gap-2 ${primary
      ? 'bg-emerald-800 hover:bg-emerald-900 text-white shadow-sm'
      : 'bg-white hover:bg-slate-50 text-slate-800 border border-slate-200'}`}
  >
    <Icon className="w-4 h-4" /> {label}
  </button>
);

const StatCard = ({ label, value, icon: Icon, tone = 'emerald' }) => {
  const toneMap = {
    emerald: 'bg-emerald-50 text-emerald-700',
    green:   'bg-green-50 text-green-700',
    amber:   'bg-amber-50 text-amber-700',
    slate:   'bg-slate-100 text-slate-700',
  };
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-4 flex items-center gap-3">
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${toneMap[tone] || toneMap.emerald}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="text-2xl font-bold text-slate-900 tabular-nums mt-0.5">{value}</div>
      </div>
    </div>
  );
};

const PageNumbers = ({ current, total, onPage }) => {
  // Show up to 5 numbers around current
  const nums = [];
  const maxVisible = 5;
  let start = Math.max(1, current - 2);
  let end = Math.min(total, start + maxVisible - 1);
  if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
  for (let i = start; i <= end; i += 1) nums.push(i);
  return (
    <div className="flex items-center gap-1">
      {nums.map((n) => (
        <button
          key={n}
          onClick={() => onPage(n)}
          className={`h-9 w-9 rounded-lg text-sm font-medium border transition ${n === current
            ? 'bg-emerald-50 border-emerald-500 text-emerald-800'
            : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          data-testid={`sales-page-${n}`}
        >
          {n}
        </button>
      ))}
    </div>
  );
};

const Th = ({ children, className = '' }) => (
  <th className={`px-3 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-500 ${className}`}>{children}</th>
);

const SortTh = ({ label, sortKey, sort, onSort, align = 'left' }) => {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`px-3 py-3 text-[10px] font-semibold uppercase tracking-widest text-slate-500 cursor-pointer hover:bg-slate-100 select-none transition ${align === 'right' ? 'text-right' : 'text-left'}`}
      data-testid={`sales-sort-${sortKey}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        <Icon className={`w-3 h-3 ${active ? 'text-emerald-700' : 'text-slate-300'}`} />
      </span>
    </th>
  );
};

const IconBtn = ({ children, onClick, title, danger, testid }) => (
  <button
    onClick={onClick}
    title={title}
    data-testid={testid}
    className={`p-1.5 rounded-md border transition ${danger
      ? 'border-red-200 text-red-700 hover:bg-red-50'
      : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
  >
    {children}
  </button>
);

const Row = ({ label, value, highlight }) => (
  <div className={`flex items-center justify-between ${highlight ? 'pt-1 border-t border-slate-100' : ''}`}>
    <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
    {value}
  </div>
);

const EmptyState = ({ onAdd, onImport }) => (
  <div className="rounded-2xl bg-white border-2 border-dashed border-slate-300 p-12 text-center space-y-4">
    <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center">
      <Package className="w-6 h-6 text-slate-500" />
    </div>
    <div>
      <div className="text-lg font-bold text-slate-900">Belum ada produk</div>
      <div className="text-sm text-slate-500 mt-1">Tambahkan produk atau import dari file.</div>
    </div>
    <div className="flex items-center justify-center gap-2">
      <ActionBtn onClick={onAdd} icon={Plus} label="Tambah Produk" primary testid="sales-empty-add" />
      <ActionBtn onClick={onImport} icon={Upload} label="Import" testid="sales-empty-import" />
    </div>
  </div>
);

/* ---------- Add/Edit Modal ---------- */
const ProductFormModal = ({ initial, categories, brandOptions, onClose, onSaved }) => {
  const [customCategories, setCustomCategories] = useState([]);
  const allCategories = useMemo(() => {
    const set = new Set([...(categories || []), ...customCategories]);
    if (initial?.category) set.add(initial.category);
    return Array.from(set);
  }, [categories, customCategories, initial]);

  const handleAddCategory = () => {
    const raw = window.prompt('Nama kategori baru:');
    if (!raw) return;
    const name = raw.trim();
    if (!name) return;
    // Match case-insensitive with existing → reuse canonical value
    const existing = allCategories.find((c) => c.toLowerCase() === name.toLowerCase());
    const finalName = existing || (name.charAt(0).toUpperCase() + name.slice(1).toLowerCase());
    if (!existing) setCustomCategories((s) => [...s, finalName]);
    setField('category', finalName);
    toast.success(`Kategori "${finalName}" ditambahkan`);
  };

  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => ({
    category:     initial?.category || 'Raket',
    brand:        initial?.brand || '',
    name:         initial?.name || '',
    price_list:   initial?.price_list ? new Intl.NumberFormat('id-ID').format(initial.price_list) : '',
    disc1:        initial?.disc1 || '',
    disc2:        initial?.disc2 || '',
    disc3:        initial?.disc3 || '',
    disc4:        initial?.disc4 || '',
    nett_manual:  initial?.nett_price ? new Intl.NumberFormat('id-ID').format(initial.nett_price) : '',
    package_free: initial?.package_free || '',
    status:       initial?.status || 'active',
  }));
  // Track whether user has manually edited Nett Price
  const [nettTouched, setNettTouched] = useState(() => {
    if (!initial) return false;
    const auto = computeNett(initial.price_list, initial.disc1, initial.disc2, initial.disc3, initial.disc4);
    return Boolean(initial.nett_price) && Math.abs((initial.nett_price || 0) - auto) > 1;
  });
  const [saving, setSaving] = useState(false);

  const priceNum = parseNum(form.price_list);
  const nettAuto = computeNett(priceNum, form.disc1, form.disc2, form.disc3, form.disc4);
  const nettFinal = nettTouched
    ? parseNum(form.nett_manual)
    : nettAuto;
  const canSave = form.name.trim() && !saving;

  // Sync Nett Price input with auto-computed value when price/discounts change (unless user touched)
  useEffect(() => {
    if (nettTouched) return;
    setForm((f) => ({
      ...f,
      nett_manual: nettAuto > 0 ? new Intl.NumberFormat('id-ID').format(nettAuto) : '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nettAuto, nettTouched]);

  const save = useCallback(async () => {
    if (!canSave) {
      if (!form.name.trim()) toast.error('Nama produk wajib diisi');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        category: form.category,
        brand: form.brand.trim(),
        name: form.name.trim(),
        price_list: priceNum,
        disc1: Number(form.disc1 || 0),
        disc2: Number(form.disc2 || 0),
        disc3: Number(form.disc3 || 0),
        disc4: Number(form.disc4 || 0),
        nett_price: nettTouched ? parseNum(form.nett_manual) : 0,
        package_free: form.package_free.trim(),
        status: form.status,
      };
      if (isEdit) {
        await updateSalesProduct(initial.id, payload);
        toast.success('Produk diperbarui');
      } else {
        await createSalesProduct(payload);
        toast.success('Produk disimpan');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  }, [canSave, form, priceNum, nettTouched, isEdit, initial, onSaved]);

  // Cmd/Ctrl+S save (do not open browser Save Page)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sales-product-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Produk' : 'Tambah Produk'}</DialogTitle>
          <DialogDescription>
            Nett Price otomatis dihitung dari diskon bertingkat.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600 mb-1 block">Kategori</Label>
              <select
                value={form.category}
                onChange={(e) => {
                  if (e.target.value === '__add_new__') { handleAddCategory(); return; }
                  setField('category', e.target.value);
                }}
                className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white uppercase"
                data-testid="sales-form-category"
              >
                {allCategories.map((c) => <option key={c} value={c} className="uppercase">{c.toUpperCase()}</option>)}
                <option value="__add_new__" className="normal-case text-emerald-700 font-semibold">+ Tambah Kategori Baru</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600 mb-1 block">Brand</Label>
              <Input
                value={form.brand}
                onChange={(e) => setField('brand', e.target.value.toUpperCase())}
                list="sales-brand-options"
                placeholder="YONEX, VICTOR, LI-NING…"
                className="h-10 uppercase"
                data-testid="sales-form-brand"
              />
              <datalist id="sales-brand-options">
                {brandOptions.map((b) => <option key={b} value={b.toUpperCase()} />)}
              </datalist>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1 block">
              Nama Produk <span className="text-red-600">*</span>
            </Label>
            <Input
              value={form.name}
              onChange={(e) => setField('name', e.target.value.toUpperCase())}
              placeholder="Contoh: ASTROX 88 PLAY"
              className="h-10 uppercase"
              data-testid="sales-form-name"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1 block">Price List (Rp)</Label>
            <Input
              value={form.price_list}
              onChange={(e) => setField('price_list', formatThousands(e.target.value))}
              inputMode="numeric"
              placeholder="0"
              className="h-10 text-right font-mono text-lg"
              data-testid="sales-form-price"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i}>
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">Discount {i} (%)</Label>
                <Input
                  type="number"
                  value={form[`disc${i}`]}
                  onChange={(e) => setField(`disc${i}`, e.target.value)}
                  placeholder="0"
                  step="0.1"
                  min="0"
                  max="100"
                  className="h-10 text-right font-mono"
                  data-testid={`sales-form-disc${i}`}
                />
              </div>
            ))}
          </div>
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold">
                Nett Price {nettTouched ? '(manual)' : '(auto dari diskon)'}
              </div>
              {nettTouched && (
                <button
                  type="button"
                  onClick={() => setNettTouched(false)}
                  className="text-[11px] text-emerald-700 hover:text-emerald-900 underline"
                  data-testid="sales-form-nett-reset"
                >
                  Reset ke auto
                </button>
              )}
            </div>
            <Input
              value={form.nett_manual}
              onChange={(e) => {
                setNettTouched(true);
                setField('nett_manual', formatThousands(e.target.value));
              }}
              inputMode="numeric"
              placeholder={nettAuto > 0 ? `Auto: ${fmtRp(nettAuto)}` : 'Contoh: 110.000'}
              className="h-12 text-right font-mono text-xl bg-white border-emerald-300 focus-visible:ring-emerald-500"
              data-testid="sales-form-nett"
            />
            <div className="text-[10px] text-slate-500 text-right">
              {nettTouched
                ? 'Manual override — abaikan diskon'
                : (nettAuto > 0
                    ? `Dihitung dari ${fmtRp(priceNum)} · ${discountLabel(form.disc1, form.disc2, form.disc3, form.disc4)}`
                    : 'Kosongkan diskon & isi Nett Price langsung untuk produk tanpa diskon')}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600 mb-1 block">Paket / Free</Label>
              <Input
                value={form.package_free}
                onChange={(e) => setField('package_free', e.target.value)}
                placeholder="Batangan / Bag / Grip / dll"
                className="h-10"
                data-testid="sales-form-package"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600 mb-1 block">Status</Label>
              <select
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
                className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                data-testid="sales-form-status"
              >
                <option value="active">Aktif</option>
                <option value="inactive">Nonaktif</option>
              </select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button
            onClick={save}
            disabled={!canSave}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="sales-form-save"
          >
            {saving ? 'Menyimpan…' : 'Simpan'}
            <span className="ml-2 text-[10px] font-normal opacity-70 hidden sm:inline">⌘S</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Delete Confirm ---------- */
const DeleteConfirm = ({ product, onCancel, onConfirm }) => (
  <Dialog open={!!product} onOpenChange={(v) => !v && onCancel()}>
    <DialogContent className="max-w-sm" data-testid="sales-del-modal">
      <DialogHeader>
        <DialogTitle>Hapus produk ini?</DialogTitle>
        <DialogDescription>
          Data produk "<b>{product?.name}</b>" akan dihapus dari database.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Batal</Button>
        <Button onClick={onConfirm} className="bg-red-600 hover:bg-red-700 text-white" data-testid="sales-del-confirm">
          Hapus
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

/* ---------- Import Modal ---------- */
const SALES_CATEGORY_LIST = ['Raket', 'Sepatu', 'Tas', 'Accessories', 'Senar', 'Grip', 'Baju', 'Celana', 'Shuttlecock'];
const normalizeCategory = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  const match = SALES_CATEGORY_LIST.find((c) => c.toLowerCase() === s.toLowerCase());
  return match || s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};
const normalizeBrand = (raw) => String(raw || '').trim().toUpperCase();

const ImportModal = ({ onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);   // parsed rows
  const [mode, setMode] = useState('upsert');
  const [importing, setImporting] = useState(false);
  const inputRef = useRef(null);

  const downloadTemplate = () => {
    const headers = ['Kategori', 'Brand', 'Produk', 'Price List',
      'Discount 1', 'Discount 2', 'Discount 3', 'Discount 4', 'Nett Price',
      'Paket / Free', 'Status'];
    const sample = [
      ['Raket', 'YONEX', 'Astrox 88 Play', 849000, 15, 5, 5, 0, 0, 'Batangan', 'active'],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...sample]);
    ws['!cols'] = [
      { wch: 14 }, { wch: 14 }, { wch: 30 },
      { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 14 },
      { wch: 16 }, { wch: 10 },
    ];
    // Style header row
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let c = 0; c <= range.e.c; c += 1) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
      if (cell) cell.s = {
        font: { bold: true, color: { rgb: 'FFFFFFFF' } },
        fill: { patternType: 'solid', fgColor: { rgb: 'FF0F172A' } },
        alignment: { horizontal: 'center', vertical: 'center' },
      };
    }
    // Rp format on price + nett + pct on discounts
    const rpFmt = '"Rp"#,##0';
    const pctFmt = '0.0"%"';
    for (let r = 1; r <= range.e.r; r += 1) {
      for (const c of [3, 8]) { // Price List, Nett Price
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (cell && typeof cell.v === 'number') cell.z = rpFmt;
      }
      for (const c of [4, 5, 6, 7]) {
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (cell && typeof cell.v === 'number') cell.z = pctFmt;
      }
    }
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'Template_Sales_Price_Catalog.xlsx');
    toast.success('Template diunduh — isi lalu upload kembali');
  };

  const parseFile = async (f) => {
    if (!f) return;
    setFile(f);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const mapped = json.map((r) => ({
        id:            String(r.ID || r.id || '').trim(),
        category:      normalizeCategory(r.Kategori || r.category || ''),
        brand:         normalizeBrand(r.Brand || r.brand || ''),
        name:          String(r.Produk || r.Nama || r.name || '').trim(),
        price_list:    parseNum(r['Price List'] || r.price_list),
        disc1:         parseNum(r['Discount 1'] || r.disc1),
        disc2:         parseNum(r['Discount 2'] || r.disc2),
        disc3:         parseNum(r['Discount 3'] || r.disc3),
        disc4:         parseNum(r['Discount 4'] || r.disc4),
        nett_price:    parseNum(r['Nett Price'] || r.nett_price || r.Netto),
        package_free:  String(r['Paket / Free'] || r.package_free || '').trim(),
        status:        String(r.Status || r.status || 'active').trim().toLowerCase() === 'inactive' ? 'inactive' : 'active',
      })).filter((r) => r.name);
      setRows(mapped);
      if (!mapped.length) toast.error('Tidak ada baris valid di file');
    } catch (e) {
      toast.error('Gagal membaca file. Pastikan format XLSX/CSV benar.');
    }
  };

  const doImport = async () => {
    if (!rows.length) return;
    setImporting(true);
    try {
      const r = await importSalesProducts(mode, rows);
      toast.success(`Import selesai: ${r.inserted} baru · ${r.updated} update · ${r.skipped} lewat`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import gagal');
    } finally { setImporting(false); }
  };

  const [dragging, setDragging] = useState(false);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="sales-import-modal">
        <DialogHeader>
          <DialogTitle>Import Produk</DialogTitle>
          <DialogDescription>
            Support format .xlsx, .xls, .csv. Header kolom: Kategori, Brand, Produk, Price List, Discount 1-4, Nett Price, Paket / Free, Status. Kategori & Brand otomatis dinormalisasi ("yonex" → "YONEX", "raket" → "Raket") agar langsung cocok dengan data yang sudah ada.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Template download tip */}
          <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
              <Download className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-800">Belum punya file?</div>
              <div className="text-xs text-slate-600">Unduh template Excel dengan header & contoh isi produk.</div>
            </div>
            <button
              onClick={downloadTemplate}
              className="h-9 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold whitespace-nowrap"
              data-testid="sales-import-template"
            >
              Download Template
            </button>
          </div>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault(); setDragging(false);
              const f = e.dataTransfer.files?.[0];
              if (f) parseFile(f);
            }}
            className={`rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition ${dragging ? 'border-blue-500 bg-blue-50' : 'border-slate-300 bg-slate-50'}`}
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            <div className="text-sm font-medium text-slate-700">
              {file ? file.name : 'Drag & Drop file di sini, atau klik untuk pilih'}
            </div>
            <div className="text-xs text-slate-500 mt-1">.xlsx, .xls, .csv</div>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => parseFile(e.target.files?.[0])}
              data-testid="sales-import-file"
            />
          </div>

          {rows.length > 0 && (
            <>
              <div className="text-sm font-medium text-slate-800">
                {rows.length} produk ditemukan
              </div>
              <div className="rounded-lg border border-slate-200 overflow-hidden max-h-40 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-slate-600">Produk</th>
                      <th className="px-2 py-1.5 text-left text-slate-600">Brand</th>
                      <th className="px-2 py-1.5 text-right text-slate-600">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 20).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1 text-slate-800">{r.name}</td>
                        <td className="px-2 py-1 text-slate-600">{r.brand}</td>
                        <td className="px-2 py-1 text-right font-mono text-slate-700">{fmtRp(r.price_list)}</td>
                      </tr>
                    ))}
                    {rows.length > 20 && (
                      <tr><td colSpan={3} className="px-2 py-1 text-center text-slate-400">…dan {rows.length - 20} lagi</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Mode Import</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: 'add', l: 'Tambah Data Baru' },
                    { v: 'update', l: 'Update by ID' },
                    { v: 'upsert', l: 'Tambah + Update' },
                  ].map((o) => (
                    <button
                      key={o.v}
                      onClick={() => setMode(o.v)}
                      className={`h-10 rounded-lg border text-xs font-medium ${mode === o.v ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-700'}`}
                      data-testid={`sales-import-mode-${o.v}`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button
            disabled={!rows.length || importing}
            onClick={doImport}
            className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            data-testid="sales-import-confirm"
          >
            {importing ? 'Mengimport…' : `Import ${rows.length} Produk`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Export Modal ---------- */
const ExportModal = ({ onClose, selectedCount, onExport }) => {
  const [scope, setScope] = useState(selectedCount > 0 ? 'selected' : 'all');
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="sales-export-modal">
        <DialogHeader>
          <DialogTitle>Export Produk</DialogTitle>
          <DialogDescription>Pilih data yang ingin diekspor dan format-nya.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Data</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setScope('all')}
                className={`h-11 rounded-lg border text-sm font-medium ${scope === 'all' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-700'}`}
                data-testid="sales-export-scope-all"
              >
                Semua Produk
              </button>
              <button
                onClick={() => setScope('selected')}
                disabled={!selectedCount}
                className={`h-11 rounded-lg border text-sm font-medium disabled:opacity-40 ${scope === 'selected' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-700'}`}
                data-testid="sales-export-scope-selected"
              >
                Terpilih ({selectedCount})
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Format</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => onExport('xlsx', scope)}
                className="h-11 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
                data-testid="sales-export-xlsx"
              >
                Excel (.xlsx)
              </button>
              <button
                onClick={() => onExport('csv', scope)}
                className="h-11 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold"
                data-testid="sales-export-csv"
              >
                CSV (.csv)
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Catalog Modal (Copy-Paste Text + Copy Table Image) ---------- */
const CatalogModal = ({ items, onClose, initialFields, onLoadPreset }) => {
  const previewRef = useRef(null);
  const tableRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [fields, setFields] = useState(() => initialFields || {
    price_list: true,
    discount: true,
    nett_price: true,
    paket: true,
  });
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [showSavePreset, setShowSavePreset] = useState(false);

  const loadPresets = async () => {
    try { setPresets(await listCatalogPresets()); }
    catch { /* silent */ }
  };
  useEffect(() => { loadPresets(); }, []);

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.error('Nama preset wajib diisi'); return; }
    if (!items.length) { toast.error('Pilih minimal 1 produk dulu'); return; }
    try {
      await createCatalogPreset({
        name,
        product_ids: items.map((p) => p.id),
        fields,
      });
      toast.success(`Preset "${name}" disimpan`);
      setPresetName('');
      setShowSavePreset(false);
      loadPresets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan preset');
    }
  };

  const loadPreset = (p) => {
    if (onLoadPreset) onLoadPreset(p.product_ids, p.fields);
  };

  const removePreset = async (id, name, e) => {
    e.stopPropagation();
    if (!window.confirm(`Hapus preset "${name}"?`)) return;
    try { await deleteCatalogPreset(id); toast.success('Preset dihapus'); loadPresets(); }
    catch { toast.error('Gagal menghapus preset'); }
  };

  const toggleField = (k) => setFields((f) => ({ ...f, [k]: !f[k] }));

  const fmtRpTight = (n) => {
    if (!n && n !== 0) return '—';
    const num = Number(n);
    if (!Number.isFinite(num) || num <= 0) return '—';
    return 'Rp' + Math.round(num).toLocaleString('id-ID');
  };

  const fmtNumDots = (n) => {
    if (!n && n !== 0) return '—';
    const num = Number(n);
    if (!Number.isFinite(num) || num <= 0) return '—';
    return Math.round(num).toLocaleString('id-ID');
  };

  const catalogText = useMemo(() => {
    const lines = [];
    items.forEach((p, idx) => {
      const title = [(p.brand || '').toUpperCase(), (p.name || '').toUpperCase()]
        .filter(Boolean).join(' ');
      lines.push(title || '(TANPA NAMA)');
      if (fields.price_list) lines.push(`Price List : ${fmtRpTight(p.price_list)}`);
      if (fields.discount) {
        const d = discountLabel(p.disc1, p.disc2, p.disc3, p.disc4);
        lines.push(`Disc : ${d === '—' ? '—' : d}`);
      }
      if (fields.nett_price) lines.push(`Netto : ${fmtRpTight(p.nett_price)}`);
      if (fields.paket) lines.push(`Paket : ${p.package_free || '—'}`);
      if (idx < items.length - 1) lines.push('');
    });
    return lines.join('\n');
  }, [items, fields]);

  const anyFieldOn = Object.values(fields).some(Boolean);

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(catalogText);
      toast.success('Katalog disalin ke clipboard');
    } catch {
      const ta = previewRef.current;
      if (ta) {
        ta.focus();
        ta.select();
        try { document.execCommand('copy'); toast.success('Katalog disalin'); }
        catch { toast.error('Gagal menyalin — pilih manual & tekan Ctrl+C'); }
      }
    }
  };

  const copyTableImage = async () => {
    if (!tableRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(tableRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) { toast.error('Gagal membuat gambar'); return; }
      try {
        // eslint-disable-next-line no-undef
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        toast.success('Gambar tabel disalin — paste di WhatsApp/chat');
      } catch (err) {
        // Fallback: download if clipboard image API unavailable
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const stamp = new Date().toISOString().slice(0, 10);
        a.href = url; a.download = `SalesPriceCatalog_${stamp}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.info('Browser tidak support copy-image, gambar diunduh sebagai file');
      }
    } finally { setBusy(false); }
  };

  const FIELD_OPTIONS = [
    { key: 'price_list', label: 'Price List' },
    { key: 'discount',   label: 'Discount' },
    { key: 'nett_price', label: 'Nett Price' },
    { key: 'paket',      label: 'Paket' },
  ];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto" data-testid="sales-catalog-modal">
        <DialogHeader>
          <DialogTitle>Katalog Copy-Paste</DialogTitle>
          <DialogDescription>
            {items.length} produk siap disalin. Pilih data yang ingin dikirimkan ke customer.
          </DialogDescription>
        </DialogHeader>

        {/* Presets bar */}
        <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              Preset Katalog
            </div>
            <button
              onClick={() => setShowSavePreset((v) => !v)}
              className="text-xs font-semibold text-emerald-800 hover:text-emerald-900"
              data-testid="sales-preset-toggle-save"
            >
              + Simpan Preset
            </button>
          </div>
          {showSavePreset && (
            <div className="flex items-center gap-2">
              <Input
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="Nama preset (mis. Customer A - Raket Yonex)"
                className="h-9 flex-1"
                data-testid="sales-preset-name"
              />
              <button
                onClick={savePreset}
                disabled={!presetName.trim() || !items.length}
                className="h-9 px-3 rounded-md bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-semibold disabled:opacity-50"
                data-testid="sales-preset-save"
              >
                Simpan
              </button>
            </div>
          )}
          {presets.length === 0 ? (
            <div className="text-xs text-slate-400 italic">Belum ada preset — simpan katalog untuk load cepat nanti.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {presets.map((p) => (
                <div key={p.id}
                  className="group inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full border border-slate-200 bg-white hover:border-emerald-400 transition"
                >
                  <button
                    onClick={() => loadPreset(p)}
                    className="text-xs font-medium text-slate-700"
                    data-testid={`sales-preset-load-${p.id}`}
                  >
                    {p.name} <span className="text-slate-400 ml-1">({(p.product_ids || []).length})</span>
                  </button>
                  <button
                    onClick={(e) => removePreset(p.id, p.name, e)}
                    className="w-5 h-5 rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center"
                    title="Hapus preset"
                    data-testid={`sales-preset-del-${p.id}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Field selection */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
            Tampilkan data
          </div>
          <div className="flex flex-wrap gap-2">
            {FIELD_OPTIONS.map((f) => (
              <label
                key={f.key}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-xs font-medium transition ${
                  fields[f.key]
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                    : 'bg-white border-slate-200 text-slate-600'
                }`}
                data-testid={`sales-catalog-field-${f.key}`}
              >
                <input
                  type="checkbox"
                  checked={fields[f.key]}
                  onChange={() => toggleField(f.key)}
                  className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                {f.label}
              </label>
            ))}
          </div>
          {!anyFieldOn && (
            <div className="text-xs text-red-600 mt-2">Pilih minimal 1 field untuk ditampilkan.</div>
          )}
        </div>

        {/* Text preview */}
        <div className="relative">
          <textarea
            ref={previewRef}
            value={catalogText}
            readOnly
            className="w-full min-h-[300px] max-h-[45vh] resize-y rounded-xl border border-slate-200 bg-slate-50 p-4 font-mono text-[13px] leading-6 text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="sales-catalog-text"
            onFocus={(e) => e.target.select()}
          />
          <button
            onClick={copyText}
            title="Salin"
            className="absolute top-2 right-2 h-8 w-8 rounded-md bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center text-slate-600"
            data-testid="sales-catalog-copy-icon"
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={onClose}>Tutup</Button>
          <div className="flex gap-2">
            <Button
              onClick={copyTableImage}
              disabled={busy || !anyFieldOn}
              className="bg-slate-800 hover:bg-slate-900 text-white gap-1.5 disabled:opacity-50"
              data-testid="sales-catalog-copy-image"
            >
              <ImageDown className="w-4 h-4" /> {busy ? 'Rendering…' : 'Copy Gambar Tabel'}
            </Button>
            <Button
              onClick={copyText}
              disabled={!anyFieldOn}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 disabled:opacity-50"
              data-testid="sales-catalog-copy"
            >
              <Copy className="w-4 h-4" /> Copy Katalog
            </Button>
          </div>
        </DialogFooter>

        {/* Hidden table (off-screen) — snapshotted by html2canvas */}
        <div style={{ position: 'fixed', left: -99999, top: 0, zIndex: -1, pointerEvents: 'none' }} aria-hidden="true">
          <div
            ref={tableRef}
            style={{ padding: 20, background: '#ffffff', fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            {/* Header: small logo + PRICE LIST title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <img
                src="/assets/logo-mb.png"
                alt="Main Bulutangkis"
                crossOrigin="anonymous"
                style={{ height: 44, width: 'auto' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', letterSpacing: 1 }}>
                PRICE LIST
              </div>
            </div>
            <table style={{ borderCollapse: 'collapse', fontSize: 14, color: '#0f172a' }}>
              <thead>
                <tr>
                  <th style={cellHeadStyle(50)}>No</th>
                  <th style={cellHeadStyle(320, 'left')}>Model</th>
                  {fields.price_list && <th style={cellHeadStyle(150, 'right')}>Retail Price</th>}
                  {fields.discount   && <th style={cellHeadStyle(120)}>Discount</th>}
                  {fields.nett_price && <th style={cellHeadStyle(150, 'right')}>Netto</th>}
                  {fields.paket      && <th style={cellHeadStyle(180, 'left')}>Paket</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((p, i) => {
                  const bg = i % 2 === 0 ? '#ffffff' : '#f1f5f9';
                  return (
                    <tr key={p.id} style={{ background: bg }}>
                      <td style={cellStyle('center')}>{i + 1}</td>
                      <td style={cellStyle('left')}>
                        {[p.brand, p.name].filter(Boolean).join(' ')}
                      </td>
                      {fields.price_list && <td style={cellStyle('right', 'tabular')}>{fmtNumDots(p.price_list)}</td>}
                      {fields.discount && (
                        <td style={{ ...cellStyle('center'), color: '#dc2626', fontWeight: 700 }}>
                          {discountLabel(p.disc1, p.disc2, p.disc3, p.disc4)}
                        </td>
                      )}
                      {fields.nett_price && (
                        <td style={{ ...cellStyle('right', 'tabular'), color: '#15803d', fontWeight: 700 }}>
                          {fmtNumDots(p.nett_price)}
                        </td>
                      )}
                      {fields.paket && <td style={cellStyle('left')}>{p.package_free || '—'}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const cellHeadStyle = (width, align = 'center') => ({
  background: '#1e3a5f',
  color: '#ffffff',
  fontWeight: 700,
  fontSize: 14,
  padding: '10px 12px',
  border: '1px solid #ffffff',
  textAlign: align,
  width,
});
const cellStyle = (align = 'center', variant = 'normal') => ({
  padding: '8px 12px',
  border: '1px solid #cbd5e1',
  textAlign: align,
  fontVariantNumeric: variant === 'tabular' ? 'tabular-nums' : 'normal',
});
